import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';

export class AuthError extends Error {
  constructor(friendly, cause) {
    super(friendly);
    this.name = 'AuthError';
    this.friendly = friendly;
    this.cause = cause;
  }
}

function poolFor(config) {
  return new CognitoUserPool({
    UserPoolId: config.userPoolId,
    ClientId: config.userPoolClientId,
  });
}

/**
 * SRP: the password proves knowledge without ever being sent.
 * Cognito is what rejects a wrong password — not this code.
 */
export function signIn(config, username, password) {
  const user = new CognitoUser({ Username: username, Pool: poolFor(config) });
  const details = new AuthenticationDetails({ Username: username, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: async (session) => {
        try {
          resolve(await exchange(config, session.getIdToken().getJwtToken()));
        } catch (err) {
          reject(new AuthError('Signed in, but could not reach S3. Try again.', err));
        }
      },
      onFailure: (err) => {
        // preventUserExistenceErrors makes Cognito return the same code for a
        // bad username and a bad password, so this message must not tell them
        // apart either.
        const friendly =
          err?.code === 'NotAuthorizedException'
            ? 'Wrong username or password.'
            : err?.code === 'PasswordResetRequiredException'
              ? 'This account needs its password reset from the AWS CLI.'
              : 'Could not sign in. Check your connection and try again.';
        reject(new AuthError(friendly, err));
      },
      newPasswordRequired: () => {
        reject(
          new AuthError(
            'This account still has a temporary password. Set a permanent one with the AWS CLI.',
          ),
        );
      },
    });
  });
}

/**
 * The sign-in already on this device, or `null`.
 *
 * `amazon-cognito-identity-js` has been writing the tokens to localStorage all
 * along; this is the page finally looking. `getSession` spends the refresh
 * token to mint a fresh id token, so a month of daily use never sees the form
 * again -- the refresh token is good for 30 days (`app-stack.ts`).
 *
 * Every failure is `null`, never a throw. This runs inside `boot()`, before
 * there is anything on screen, and an exception there leaves a page with no
 * login form and nothing to press. An expired refresh token is not a fault
 * either: it is an ordinary morning on which you sign in again.
 *
 * Takes a pool rather than a config so the decision is testable without a
 * network or a user pool -- the exchange for AWS credentials is `signIn`'s
 * business and stays there.
 */
export async function sessionFromPool(pool) {
  const user = pool.getCurrentUser();
  if (!user) return null;

  return new Promise((resolve) => {
    try {
      user.getSession((err, session) => {
        if (err || !session?.isValid()) return resolve(null);
        resolve({ idToken: session.getIdToken().getJwtToken(), username: user.getUsername() });
      });
    } catch {
      resolve(null);
    }
  });
}

/**
 * The same session, exchanged for AWS credentials, ready to use like `signIn`'s.
 */
export async function restoreSession(config) {
  const restored = await sessionFromPool(poolFor(config));
  if (!restored) return null;
  try {
    return { ...(await exchange(config, restored.idToken)), username: restored.username };
  } catch {
    // Signed in as far as Cognito is concerned, but the identity pool would
    // not deal. Nothing works without credentials, so this is a sign-in that
    // did not happen rather than a half-open session to paper over.
    return null;
  }
}

async function exchange(config, idToken) {
  const login = `cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}`;
  const credentials = fromCognitoIdentityPool({
    client: new CognitoIdentityClient({ region: config.region }),
    identityPoolId: config.identityPoolId,
    logins: { [login]: idToken },
  });

  // Resolve once up front so a misconfigured pool fails at sign-in rather than
  // halfway through a 40-minute upload. The provider caches the result.
  const resolved = await credentials();
  return { idToken, identityId: resolved.identityId, credentials };
}

export function signOut(config, username) {
  if (!username) return;
  new CognitoUser({ Username: username, Pool: poolFor(config) }).signOut();
}
